import type { VFC } from 'react';
import { memo, useCallback } from 'react';

import { addUFOCustomMark } from '@atlaskit/react-ufo/custom-mark';

import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';

import { ActionTiming, usePerformanceAction } from '../usePerformanceAction.tsx';
import { PAGE_SEGMENT_MARK_PREFIX_START } from '../metric-keys/index.ts';
import type { PageSegmentLoadStartProps } from '../types/PageSegmentLoadStartProps.ts';

import { useIsEmbeddedTracking } from './EmbeddedConfluenceTrackingContext.tsx';

const noop = () => {};
const EMBEDDED_PREFIX = 'embedded-';

const UFOSegmentLoadStartComponent: VFC<PageSegmentLoadStartProps> = memo(
	({ metric, onComplete = noop, timing = ActionTiming.RENDER_BODY }) => {
		const isEmbedded = useIsEmbeddedTracking();
		const prefixedKey = isEmbedded ? `${EMBEDDED_PREFIX}${metric.key}` : metric.key;

		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			SSRMeasures.markMeasureStart(`ssr-app/render/${prefixedKey}`);
		}

		const handleSegmentLoadStart = useCallback(() => {
			if (metric) {
				addUFOCustomMark(`${PAGE_SEGMENT_MARK_PREFIX_START}:${prefixedKey}`);
				onComplete();
			}
		}, [metric, onComplete, prefixedKey]);

		usePerformanceAction(handleSegmentLoadStart, timing);
		return null;
	},
);

export const UFOSegmentLoadStart = withTransparentErrorBoundary({
	attribution: Attribution.BACKBONE,
})(UFOSegmentLoadStartComponent);
